import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';

interface OtpVerificationInputProps {
  title: string;
  description: string;
  onVerify: (code: string) => Promise<void>;
  onBack: () => void;
  onResend?: () => Promise<void>;
  isPending?: boolean;
  autoSubmit?: boolean;
}

export function OtpVerificationInput({
  title,
  description,
  onVerify,
  onBack,
  onResend,
  isPending = false,
  autoSubmit = true,
}: OtpVerificationInputProps) {
  const { t } = useTranslation('common');
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Auto-submit when code is complete
  useEffect(() => {
    if (autoSubmit && code.length === 6 && !isPending) {
      onVerify(code);
    }
  }, [code, autoSubmit, isPending, onVerify]);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!onResend || resendCooldown > 0) return;
    
    setIsResending(true);
    try {
      await onResend();
      setResendCooldown(60); // 60 second cooldown
      setCode(''); // Clear the code
    } catch (_error) {
      // Error handled by mutation
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = () => {
    if (code.length === 6) {
      onVerify(code);
    }
  };

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={setCode}
            disabled={isPending}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            onClick={handleVerify}
            disabled={code.length !== 6 || isPending}
            className="w-full"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t('auth.verify', 'Verify')}
          </Button>

          {onResend && (
            <Button
              variant="ghost"
              onClick={handleResend}
              disabled={resendCooldown > 0 || isResending}
              className="w-full"
            >
              {isResending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {resendCooldown > 0
                ? t('auth.resendIn', { resendCooldown, defaultValue: 'Resend in {{resendCooldown}}s' })
                : t('auth.resendCode', 'Resend code')}
            </Button>
          )}

          <Button variant="outline" onClick={onBack} className="w-full">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back', 'Back')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
